import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "How It Works", to: "/#how", type: "anchor" },
  { label: "The Reveal", to: "/#pillars", type: "anchor" },
  { label: "About", to: "/about", type: "route" },
  { label: "Access", to: "/#pricing", type: "anchor" },
  { label: "Contact", to: "/contact", type: "route" },
  { label: "Take The Quiz", to: "/quiz", type: "route" },
];

export default function Nav({ variant = "light" }) {
  const isDark = variant === "dark";
  const [open, setOpen] = useState(false);

  return (
    <nav
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: isDark ? "rgba(14,10,31,0.65)" : "rgba(250,248,243,0.85)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: `0.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(30,27,22,0.07)"}`,
      }}
    >
      <div className="flex items-center justify-between" style={{ padding: "16px 32px" }}>
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif"
          style={{
            fontSize: 20,
            fontWeight: 500,
            letterSpacing: "0.01em",
            color: isDark ? "#FAF8F3" : "var(--mc-text)",
            textDecoration: "none",
          }}
        >
          Marrsing<span style={{ color: "var(--mc-blue)" }}>Clause</span>
          <sup style={{ fontSize: 10, marginLeft: 1, opacity: 0.6 }}>®</sup>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center" style={{ gap: 28 }}>
          <a href="/#how" data-testid="nav-how" className="mc-nav-link" style={navLink(isDark)}>How It Works</a>
          <a href="/#pillars" data-testid="nav-pillars" className="mc-nav-link" style={navLink(isDark)}>The Reveal</a>
          <Link to="/about" data-testid="nav-about" className="mc-nav-link" style={navLink(isDark)}>About</Link>
          <a href="/#pricing" data-testid="nav-pricing" className="mc-nav-link" style={navLink(isDark)}>Access</a>
          <Link to="/contact" data-testid="nav-contact" className="mc-nav-link" style={navLink(isDark)}>Contact</Link>
          <Link to="/quiz" data-testid="nav-quiz-cta" style={quizPill(true)}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(111,199,242,0.28)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(111,199,242,0.16)")}
          >
            Take the Quiz
          </Link>
        </div>

        {/* Mobile controls: Quiz pill stays visible + hamburger */}
        <div className="flex md:hidden items-center" style={{ gap: 12 }}>
          <Link to="/quiz" data-testid="nav-quiz-mobile" style={quizPill(false)}>Quiz</Link>
          <button
            type="button"
            data-testid="nav-hamburger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              border: `0.5px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(47,158,216,0.32)"}`,
              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(111,199,242,0.12)",
              color: isDark ? "#FAF8F3" : "var(--mc-blue-deep)",
              cursor: "pointer",
            }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden"
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "8px 24px 24px",
            background: isDark ? "rgba(14,10,31,0.96)" : "rgba(250,248,243,0.98)",
            borderTop: `0.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(30,27,22,0.07)"}`,
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        >
          {NAV_ITEMS.map((item) => {
            const testid = `mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
            const style = mobileLink(isDark);
            return item.type === "route" ? (
              <Link key={item.label} to={item.to} data-testid={testid} style={style} onClick={() => setOpen(false)}>
                {item.label}
              </Link>
            ) : (
              <a key={item.label} href={item.to} data-testid={testid} style={style} onClick={() => setOpen(false)}>
                {item.label}
              </a>
            );
          })}
        </div>
      )}
    </nav>
  );
}

function quizPill(desktop) {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: desktop ? 11 : 10,
    letterSpacing: desktop ? "0.1em" : "0.08em",
    textTransform: "uppercase",
    color: "var(--mc-blue-deep)",
    background: "rgba(111,199,242,0.16)",
    border: "0.5px solid rgba(111,199,242,0.42)",
    borderRadius: 999,
    padding: desktop ? "9px 20px" : "8px 16px",
    textDecoration: "none",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
  };
}

function mobileLink(isDark) {
  return {
    fontSize: 15,
    fontWeight: 500,
    letterSpacing: "0.04em",
    color: isDark ? "rgba(250,248,243,0.88)" : "var(--mc-text)",
    textDecoration: "none",
    padding: "16px 4px",
    borderBottom: `0.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(30,27,22,0.08)"}`,
  };
}

function navLink(isDark) {
  return {
    fontSize: 12.5,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: isDark ? "rgba(250,248,243,0.7)" : "var(--mc-text-3)",
    textDecoration: "none",
    transition: "color 0.2s",
  };
}
